import React, { useEffect, useRef, useState } from 'react';
import './sale-form.styles.scss';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import ManageContentInformation from '../../create-elements/blocks/information.component';
import ManageContentIconCropped from '../../create-elements/blocks/icon.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageContentDates from '../../create-elements/blocks/dates.component';
import ItemSelector from '../../create-elements/blocks/item-selector.component';
import SaleAmount from '../../create-elements/blocks/sale-amount.component';
import { useSelector } from 'react-redux';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { RandomString } from '@/utils/randomString';
import { UseCloudFunction } from '@/utils/firebase.utils';
import useAnalytics from '@/utils/useAnalytics';

const SaleForm = ({ gameData, gameElements, gameStats }) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const membership = useSelector(state => state.userData.membership);
    const [alertMessage, setAlertMessage] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [formData, setFormData] = useState({});
    const [existingDescription, setExistingDescription] = useState(null);
    const [clearEditor, setClearEditor] = useState(false);
    const [loading, setLoading] = useState(false);
    const [storeItems, setStoreItems] = useState(null);
    const { trackEvent, trackException } = useAnalytics();
    const { saleId } = useParams();
    const dataRef = useRef();
    const approveRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!gameElements) return;
        let obj = {}
        Object.values(gameElements)
            .filter(e => e.type === 'item' && e.opt_market && e.opt_marketPrice)
            .map(e => { obj[e.id] = e })
        setStoreItems(obj);
    }, [gameElements])

    function saveData(key, value) {
        dataRef.current = { ...dataRef.current, ...{ [key]: value } };
        setFormData(dataRef.current);
    }

    async function handleSubmit() {
        if (location.pathname.includes('/preview/manager/')) {
            setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
            return;
        }
        let tempData = { ...dataRef.current };
        console.log(tempData);
        if (
            !tempData.name ||
            !tempData.opt_itemList ||
            !tempData.opt_salePercentage
        ) {
            setAlertMessage('Please complete the required parts of the form before continuing.');
            return;
        }
        setConfirmMessage('Are you sure you are ready to save this Sale? If the current date falls within your chosen start and end dates or you did not choose a start date, the sale will be effective immediately.')
        approveRef.current = () => {
            completeSave(tempData);
        }

        async function completeSave(tempData) {
            setLoading(true);
            const now = new Date().getTime();
            const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
            let action = 'Edited';
            if (!tempData.id) {
                tempData.appId = userProfile.appId;
                tempData.icon = 'images/icons/sale.png';
                tempData.gameId = gameData.gameId;
                tempData.id = now + '-' + gameCode + '-sale';
                tempData.status = 'active';
                tempData.type = 'item';
                tempData.public = true;
                tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
                action = 'Created';
            }
            console.log(tempData);
            const res = await UseCloudFunction(
                'saveGameElement',
                {
                    'gameId': gameData.gameId,
                    'appId': userProfile.appId,
                    'elementData': tempData,
                    'userName': `${userProfile.fName} ${userProfile.lName}`,
                    'action': action
                }
            )
            if (res.error) {
                setAlertMessage('Something went wrong: ' + res.error)
                setLoading(false);
                trackException(`Sale ${action} Error: ${res.error}`);
                return;
            }
            setAlertMessage('Sale saved successfully!');
            setLoading(false);
            trackEvent(
                action === 'Created' ? 'sale_created' : 'sale_edited',
                'Game Manager',
                `Sale ${action.toLowerCase()} for game ${gameData.gameId}.`,
                null,
                membership
            );
            navigate(`/manage/${gameData.path}/marketplace/sales`)
        }
    }

    return (
        <div className='sale-form-container'>

            <div className='sale-form'>
                <ManageContentInformation
                    type='sale'
                    saveData={saveData}
                    formData={formData}
                    existingDescription={existingDescription}
                    clearEditor={clearEditor}
                    setClearEditor={setClearEditor}
                    textEditorLabel='Sale Explanation (optional)'
                />
                <div className='g-space-2'></div>
                <ItemSelector
                    type='sale'
                    formData={formData}
                    saveData={saveData}
                    gameElements={storeItems}
                    gameData={gameData}
                    gameStats={gameStats}
                    saveKey='opt_itemList'
                    headText="Select Items for this Sale"
                />
                <div className='g-space-2'></div>
                <SaleAmount
                    type='sale'
                    formData={formData}
                    saveData={saveData}
                />
                <div className='g-space-2'></div>
                <ManageContentDates
                    type='sale'
                    saveData={saveData}
                    formData={formData}
                    gameElements={gameElements}
                    text='Sales'
                    title='Start and End Dates'
                />
                <div className='g-space-2'></div>
                <span className='required'>* = Required</span>
                <div className='buttons'>
                    {
                        (loading)
                            ?
                            <>
                                <button type='button' className='g-button'>Cancel</button>
                                <button type='button' className='g-button'><ThreeDotsLoader /></button>
                            </>
                            :
                            <>
                                <button type='button' className='g-button' onClick={() => cancelCreate()}>Cancel</button>
                                <button type='button' className='g-button primary' onClick={() => handleSubmit()}>Save Sale</button>
                            </>
                    }
                </div>
            </div>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage}
            />
            <ModalConfirm
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current}
            />
            <FullPageLoader show={loading} />
        </div>
    )
}

export default SaleForm;